import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, IconButton } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import { useSettingValue } from 'common/hooks/useSettingValue';
import Layout from 'components/Layout';
import useFullLoadingStyles from 'components/styles/useFullLoadingStyles';
import config from 'config';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    getSessionChecked,
    getTokenCsrf,
    loginCheck,
    logoutAction,
    userHasAuthenticated,
    userIsAuthenticating,
} from 'redux/slices/authSlice';
import { getOrganization } from 'redux/slices/organizationSlice';
import { AircallDialError, setDialErrorNotifier } from 'services/aircallDiagnostics';
import { AirCallPanel } from './aircallpanel';

// Each failure gets the message that matches it. A single generic "check you are
// logged in" used to be shown for all of them, which pointed agents at a session
// problem even when the session was fine.
const DIAL_ERROR_MESSAGES = {
    [AircallDialError.NOT_MOUNTED]: 'aircall.error.notMounted',
    [AircallDialError.MISSING_NUMBER]: 'aircall.error.missingNumber',
    [AircallDialError.INVALID_NUMBER]: 'aircall.error.invalidNumber',
    [AircallDialError.NOT_LOGGED_IN]: 'aircall.error.notLoggedIn',
    [AircallDialError.LOGIN_CHECK_TIMEOUT]: 'aircall.error.loginCheckTimeout',
    [AircallDialError.DIAL_FAILED]: 'aircall.error.dialFailed',
};

const Main = () => {
    const dispatch = useDispatch();
    const [stripe] = useState(() => loadStripe(config.stripe.publicKey));

    const enableAircalls = useSettingValue(SettingsKey.ENABLE_AIRCALLS) ?? false;
    const sessionChecked = useSelector(getSessionChecked);
    const isAuthenticated = useSelector(userHasAuthenticated);
    const isAuthenticating = useSelector(userIsAuthenticating);
    const { classes } = useFullLoadingStyles();
    const tokenCsrf = useSelector(getTokenCsrf);
    const organizationCountry = useSelector(getOrganization)?.country;
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(loginCheck(window.location.href));
    }, [dispatch]);

    useEffect(() => {
        if (sessionChecked && !isAuthenticating && !isAuthenticated) {
            dispatch(
                logoutAction({
                    success: () => {
                        navigate('/login');
                    },
                    tokenCsrf,
                }),
            );
        }
    }, [sessionChecked, isAuthenticating, isAuthenticated, dispatch, navigate, tokenCsrf]);

    // Registered independently of the panel so a failure is still shown to the
    // agent when the panel is the thing that failed to load.
    useEffect(() => {
        setDialErrorNotifier((error) => {
            const reason = error?.reason;
            const message = t(DIAL_ERROR_MESSAGES[reason] ?? 'aircall.dialError', {
                phone: error?.dialedPhone ?? error?.rawPhone ?? '',
            });
            // The reason code rides along in the toast so an agent reporting the
            // problem gives support the exact failure without a screen share.
            enqueueSnackbar(`${message} (aircall:${reason ?? 'unknown'})`, {
                variant: 'error',
                autoHideDuration: 10000,
            });
        });
        return () => setDialErrorNotifier(null);
    }, [enqueueSnackbar, t]);

    useEffect(() => {
        if (!enableAircalls || window.airCallPanel?.isAircallIFrameMounted()) return;
        window['airCallPanel'] = new AirCallPanel();
    }, [enableAircalls]);

    // Set separately from construction so a late organization fetch still lands,
    // without tearing down a workspace iframe that is already logged in.
    useEffect(() => {
        if (!enableAircalls || !organizationCountry) return;
        window.airCallPanel?.setDefaultCountry(organizationCountry);
    }, [enableAircalls, organizationCountry]);

    return sessionChecked && isAuthenticated ? (
        <Elements stripe={stripe}>
            {Boolean(enableAircalls) && (
                <div id="aircall-container">
                    <div id="close-phone">
                        <IconButton onClick={() => window.airCallPanel.close()}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div id="aircall"></div>
                </div>
            )}
            <Layout />
        </Elements>
    ) : (
        <div className={classes.root}>
            <CircularProgress />
        </div>
    );
};

export default Main;
